/**
 * Debug mode utility — authorisation-gated, single source of truth.
 *
 * Debug mode is stored in localStorage (key: TM_DEBUG) but is ONLY
 * activatable by platform-privileged users. The ?debug=true query
 * parameter is always stripped from the URL; it only persists the
 * flag if the current session belongs to a privileged email.
 *
 * Drivers can never enable debug mode — not via URL, not via
 * localStorage manipulation — because isDebugMode() itself checks
 * the privilege flag stored at activation time.
 */

const LS_KEY = "TM_DEBUG";
/** Stored alongside TM_DEBUG to validate the activating user */
const LS_AUTH_KEY = "TM_DEBUG_AUTH";

/** Emails that may activate debug mode */
const DEBUG_PRIVILEGED: ReadonlySet<string> = new Set([
  "aaron@transportmate.co.uk",
  "ajleaney@me.com",
]);

/**
 * Bootstrap: call once at app startup.
 * Strips ?debug param from URL. Only persists the flag if the
 * logged-in user is platform-privileged.
 */
export function bootstrapDebugMode(currentEmail?: string | null): void {
  try {
    const url = new URL(window.location.href);
    const wantsDebug = url.searchParams.get("debug") === "true";

    // Always strip the query param — never leave it in the URL
    if (url.searchParams.has("debug")) {
      url.searchParams.delete("debug");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }

    if (wantsDebug && currentEmail && DEBUG_PRIVILEGED.has(currentEmail.toLowerCase())) {
      localStorage.setItem(LS_KEY, "true");
      localStorage.setItem(LS_AUTH_KEY, currentEmail.toLowerCase());
    } else if (wantsDebug) {
      // Non-privileged user tried ?debug=true — silently ignore
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_AUTH_KEY);
    }
  } catch {
    // Ignore — SSR or restricted env
  }
}

/**
 * Check if debug mode is active AND was activated by a privileged user.
 * Even if someone manually sets TM_DEBUG=true in localStorage,
 * this returns false unless TM_DEBUG_AUTH matches a privileged email.
 */
export function isDebugMode(): boolean {
  try {
    if (localStorage.getItem(LS_KEY) !== "true") return false;
    const authEmail = localStorage.getItem(LS_AUTH_KEY);
    return !!authEmail && DEBUG_PRIVILEGED.has(authEmail.toLowerCase());
  } catch {
    return false;
  }
}

/** Enable debug mode programmatically — requires privileged email. */
export function enableDebugMode(email?: string | null): void {
  try {
    if (email && DEBUG_PRIVILEGED.has(email.toLowerCase())) {
      localStorage.setItem(LS_KEY, "true");
      localStorage.setItem(LS_AUTH_KEY, email.toLowerCase());
    }
  } catch {}
}

/** Disable debug mode programmatically. */
export function disableDebugMode(): void {
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_AUTH_KEY);
  } catch {}
}
